import React from 'react';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import { Surface } from '../layout/Surface';
import { InfoHint, type InfoHintProps } from './InfoHint';
import { Sparkline, type SparklinePoint } from './Sparkline';
import { StatusChip, type StatusTone } from './StatusChip';

export interface MetricDelta {
  /** Percentage change, signed: -4.2 for a 4.2% fall. */
  pct: number;
  /**
   * Whether this change is good news. Kept apart from the sign because down
   * is not always bad — a falling decline rate is an improvement. `null` for
   * a change that is neither.
   */
  good: boolean | null;
  /** What it is compared with, e.g. "Jul 26". Shown as "vs Jul 26". */
  comparedTo: string;
  /** The full comparison, on hover: both values and why these two. */
  detail?: string;
}

export interface MetricTrend {
  points: SparklinePoint[];
  formatValue?: (value: number) => string;
  label?: string;
}

/** "Days submit to decision" → "Days submit to " and "decision". */
const lastSpace = (s: string) => s.trimEnd().lastIndexOf(' ');
const leadingWords = (s: string) => (lastSpace(s) < 0 ? '' : s.slice(0, lastSpace(s) + 1));
const lastWord = (s: string) => s.trimEnd().slice(lastSpace(s) + 1);

export interface MetricCardProps {
  label: string;
  /** Already formatted: "$1.2M", "32.5%". */
  value: string;
  /** What the figure means, behind an info icon beside the label. */
  info?: React.ReactNode;
  /** `warning` turns the info icon yellow: the figure is worked out in a way worth reading first. */
  infoTone?: InfoHintProps['tone'];
  /** `null` for "no change to show" (then `note` says why). Omit with `note` to leave the line out. */
  delta?: MetricDelta | null;
  /** Said where the change would be when there is none to show, e.g. "Still settling". */
  note?: string;
  /** A trend line along the bottom of the card. */
  trend?: MetricTrend | null;
  loading?: boolean;
  /**
   * `tile` — one of a row of cards, on a board.
   * `hero` — a single number shown on its own, e.g. a result by itself.
   * `fill` — the number alone, centred in whatever space it is given and
   * named by the frame around it, for a widget whose header carries the
   * title. The label is not drawn twice.
   */
  size?: 'tile' | 'hero' | 'fill';
  /** No card of its own — for a figure already inside a panel. */
  bare?: boolean;
  /**
   * A second figure of the same thing, under the headline one: the premium on
   * the cases a count counts. It is the same subject in another unit, which is
   * why it shares the tile instead of taking one of its own.
   */
  secondary?: {
    /** Already formatted: "$11.13M". */
    value: string;
    /** What it is, in a word or two: "premium". */
    label?: string;
    info?: React.ReactNode;
    infoTone?: InfoHintProps['tone'];
    /**
     * Its own trend. The card splits down the middle, each figure a half with
     * its value and what it counts, when both figures have a trend (each
     * gets its own line underneath) or neither does (Open clients · cases on
     * the Daily Call Plan). When only the headline has one, the two figures
     * stack over that single trend footer.
     */
    trend?: MetricTrend | null;
  };
  /** What the headline value counts, when a second figure sits beside it: "cases". */
  unit?: string;
  /**
   * The same figure for something to measure it against, beside the value:
   * "Network 8.1%" next to one operator's 15.4%. `tone` says whether this
   * card's own figure is the better one (`good`) or the worse (`warning`).
   */
  comparison?: { label: string; tone: StatusTone; detail?: string };
}

/**
 * One headline number: its name, its value, how it moved, and its trend.
 *
 * - The change is a pill: an arrow for which way the number went, green or
 *   red for whether that is good — the two are separate, since down is not
 *   always bad. "vs Jul 26" says what it was compared with; hover for both
 *   values.
 * - With no change to show, `note` says why rather than leaving a gap.
 * - The trend runs the full width of the card's footer, where it has room
 *   to be read and hovered.
 * - While loading, the card keeps its shape so the row does not jump.
 *
 * Cards in a row line up: same label, value, change and trend positions in
 * every card, whatever is missing from any one of them.
 */
/** One figure on a card: the number, what it counts, and its note. */
function Figure({ value, unit, info, infoTone, variant, full, comparison }: {
  value: string;
  unit?: string;
  info?: React.ReactNode;
  infoTone?: InfoHintProps['tone'];
  variant: 'h1' | 'h2' | 'h3';
  full?: boolean;
  comparison?: MetricCardProps['comparison'];
}) {
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      {/* The comparison wraps under the value on a narrow card rather than
          squeezing it. */}
      <Stack direction="row" alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant={variant} noWrap sx={{ fontVariantNumeric: 'tabular-nums', ...(full && { maxWidth: '100%' }) }}>
            {value}
          </Typography>
          {info && (
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <InfoHint title={info} label={`About ${unit ?? 'this figure'}`} tone={infoTone} />
            </Box>
          )}
        </Stack>
        {comparison && (
          <Tooltip title={comparison.detail ?? ''} placement="top">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <StatusChip tone={comparison.tone} label={comparison.label} />
            </Box>
          </Tooltip>
        )}
      </Stack>
      {/* What the number counts, under it rather than beside it: two figures
          side by side need saying apart, and the width belongs to the number. */}
      {unit && (
        <Typography variant="caption" noWrap sx={(t) => ({ color: t.tokens.text.tertiary })}>
          {unit}
        </Typography>
      )}
    </Stack>
  );
}

export function MetricCard({
  label, value, info, infoTone, delta, note, trend, loading = false, size = 'tile', bare = false, secondary, unit,
  comparison,
}: MetricCardProps) {
  const hero = size === 'hero';
  // Sized by its container rather than its content: a widget tile is resized
  // by the person reading it, and the number should use what it is given.
  const fill = size === 'fill';
  // Pass neither `delta` nor `note` for a card with no change line at all —
  // a board where the trend line already says how the figure moved.
  const showChange = delta !== undefined || note !== undefined;
  // Two figures, each with its own line: the card splits instead of stacking
  // them over one shared footer.
  const split = Boolean(secondary) && Boolean(secondary?.trend) === Boolean(trend) && !fill;
  const direction = delta ? (delta.pct > 0 ? 'up' : delta.pct < 0 ? 'down' : null) : null;

  const change = delta ? (
    <Tooltip title={delta.detail ?? ''} placement="top">
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <Box
          component="span"
          sx={(t) => ({
            ...t.typography.caption,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            flexShrink: 0,
            px: 0.75,
            borderRadius: `${t.ds.radius.full}px`,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color:
              delta.good === null
                ? t.tokens.text.secondary
                : delta.good ? t.tokens.semantic.success : t.tokens.semantic.danger,
            bgcolor:
              delta.good === null
                ? t.tokens.surface.sunken
                : delta.good ? t.tokens.semantic.successSoft : t.tokens.semantic.dangerSoft,
          })}
        >
          {direction === 'up' && <ArrowUpwardRounded sx={(t) => ({ fontSize: t.typography.caption.fontSize })} />}
          {direction === 'down' && <ArrowDownwardRounded sx={(t) => ({ fontSize: t.typography.caption.fontSize })} />}
          {Math.abs(delta.pct).toFixed(1)}%
        </Box>
        {/* Wraps under itself in a narrow card rather than being cut. */}
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, minWidth: 0 })}>
          vs {delta.comparedTo}
        </Typography>
      </Stack>
    </Tooltip>
  ) : (
    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
      {note ?? ' '}
    </Typography>
  );

  const Frame = bare ? Box : Surface;
  return (
    <Frame sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', ...(fill && { height: '100%' }) }}>
      <Stack
        spacing={hero || fill ? 1 : 0.5}
        sx={{
          p: hero ? 3 : fill ? 2 : 1.5,
          flex: 1,
          minWidth: 0,
          ...(fill && { alignItems: 'center', justifyContent: 'center', textAlign: 'center' }),
        }}
      >
        {/* The name is always shown in full: it wraps rather than being cut
            off, with the info icon following its last word. In a row of
            tiles it keeps room for two lines, so the values below still line
            up when one name wraps and its neighbours don't. */}
        <Typography
          variant="overline"
          component="div"
          sx={(t) => ({
            color: t.tokens.text.secondary,
            lineHeight: 1.5,
            minWidth: 0,
            overflowWrap: 'anywhere',
            minHeight: hero || fill ? undefined : '3em',
            display: fill ? 'none' : undefined,
          })}
        >
          {info ? (
            <>
              {/* The icon is glued to the last word, so a wrap takes the two
                  together — never the icon alone on a line. */}
              {leadingWords(label)}
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {lastWord(label)}
                <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 0.5 }}>
                  <InfoHint title={info} label={`About ${label}`} tone={infoTone} />
                </Box>
              </Box>
            </>
          ) : (
            label
          )}
        </Typography>

        {split ? (loading ? (
          <Skeleton variant="rounded" height={64} />
        ) : (
          // Two figures of the same thing in different units: a half each,
          // same size, each with its own line. Neither reads as the other's
          // footnote, and neither line is scaled to the other.
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, alignItems: 'start' }}>
            <Figure value={value} unit={unit} variant="h3" comparison={comparison} />
            <Box sx={(t) => ({ pl: 1.5, borderLeft: `1px solid ${t.tokens.border.subtle}`, minWidth: 0 })}>
              <Figure
                value={secondary!.value}
                unit={secondary!.label}
                info={secondary!.info}
                infoTone={secondary!.infoTone}
                variant="h3"
              />
            </Box>
          </Box>
        )) : (
          <>
            {loading ? (
              <Skeleton variant="text" width="65%" sx={(t) => ({ ...(hero ? t.typography.h1 : t.typography.h2) })} />
            ) : (
              <Figure
                value={value}
                unit={unit}
                variant={hero || fill ? 'h1' : secondary ? 'h3' : 'h2'}
                full={fill}
                comparison={comparison}
              />
            )}

            {secondary && (loading ? (
              <Skeleton variant="text" width="55%" />
            ) : (
              <Figure
                value={secondary.value}
                unit={secondary.label}
                info={secondary.info}
                infoTone={secondary.infoTone}
                variant={hero ? 'h1' : 'h3'}
              />
            ))}
          </>
        )}

        {showChange && (loading ? <Skeleton variant="text" width="45%" /> : change)}
      </Stack>

      {split && trend && secondary?.trend && (
        <Box
          sx={(t) => ({
            px: 1.5,
            py: 1,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface.sunken,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 1.5,
          })}
        >
          {loading ? <Skeleton variant="rounded" height={24} /> : (
            <Sparkline points={trend!.points} width="fill" formatValue={trend!.formatValue} label={trend!.label} surface="sunken" />
          )}
          <Box sx={(t) => ({ pl: 1.5, borderLeft: `1px solid ${t.tokens.border.subtle}`, minWidth: 0 })}>
            {loading ? <Skeleton variant="rounded" height={24} /> : (
              <Sparkline
                points={secondary!.trend!.points}
                width="fill"
                formatValue={secondary!.trend!.formatValue}
                label={secondary!.trend!.label}
                surface="sunken"
              />
            )}
          </Box>
        </Box>
      )}

      {trend && !split && (
        <Box
          sx={(t) => ({
            px: 1.5,
            py: 1,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface.sunken,
          })}
        >
          {loading ? (
            <Skeleton variant="rounded" height={24} />
          ) : (
            <Sparkline
              points={trend.points}
              formatValue={trend.formatValue}
              label={trend.label ?? label}
              height={24}
              surface="sunken"
            />
          )}
        </Box>
      )}
    </Frame>
  );
}
